import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Stack,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Event as EventIcon,
  Cancel as CancelIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import axios from '../../utils/axios';
import { fr } from 'date-fns/locale';
import { canCancelRegistration, cancellationCutoffMessage } from '../../utils/cancellationPolicy';

const UserDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState({
    user: null,
    upcomingActivities: [],
    pastActivities: [],
    subscription: null,
  });

  useEffect(() => {
    fetchUserData();
  }, []);

  // Add event listener for user data changes
  useEffect(() => {
    const handleUserDataChange = () => {
      fetchUserData();
    };

    window.addEventListener('userDataChanged', handleUserDataChange);

    return () => {
      window.removeEventListener('userDataChanged', handleUserDataChange);
    };
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const [userRes, activitiesRes, subscriptionRes] = await Promise.all([
        axios.get('/api/users/me'),
        axios.get('/api/activities/user/registered'),
        axios.get('/api/users/me/subscription'),
      ]);

      const now = new Date();
      const activities = activitiesRes.data;
      const upcomingActivities = activities.filter(
        (activity) => new Date(activity.date) > now
      );
      const pastActivities = activities.filter(
        (activity) => new Date(activity.date) <= now
      );

      setUserData({
        user: userRes.data,
        upcomingActivities,
        pastActivities,
        subscription: subscriptionRes.data,
      });
      setError(null);
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError(t('errorFetchingUserData'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRegistration = async (activityId) => {
    if (window.confirm(t('confirmCancelRegistration'))) {
      try {
        await axios.delete(`/api/activities/${activityId}/register`);
        await fetchUserData();  // Refresh all user data
        setError(null);
      } catch (err) {
        console.error('Error canceling registration:', err);
        setError(err.response?.data?.message || t('errorCancelingRegistration') || t('errorCancelling'));
      }
    }
  };

  const handleNewsletterToggle = async () => {
    try {
      const response = await axios.put('/api/users/profile', {
        newsletterSubscribed: !userData.user?.newsletterSubscribed
      });
      
      // Update the user data with the response
      setUserData(prev => ({
        ...prev,
        user: response.data
      }));
      
      setError(null);
    } catch (err) {
      console.error('Error toggling newsletter subscription:', err);
      setError(t('errorTogglingNewsletter'));
    }
  };

  const ActivityCard = ({ activity, showCancel = false }) => {
    const canCancel = canCancelRegistration(activity);
    return (
    <Card elevation={0} sx={{ mb: 2 }}>
      <CardContent>
        <Stack spacing={2}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <Typography variant="h6">{activity.title}</Typography>
            {showCancel && canCancel && (
              <Tooltip title={t('cancelRegistration')}>
                <IconButton
                  size="small"
                  onClick={() => handleCancelRegistration(activity._id)}
                >
                  <CancelIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {showCancel && !canCancel && (
            <Typography variant="caption" color="text.secondary">
              {cancellationCutoffMessage(activity)}
            </Typography>
          )}
          <Stack spacing={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarIcon fontSize="small" color="action" />
              <Typography variant="body2">
                {format(new Date(activity.date), 'PPP', { locale: fr })}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimeIcon fontSize="small" color="action" />
              <Typography variant="body2">
                {format(new Date(activity.date), 'p', { locale: fr })} ({t('durationMinutes', { minutes: activity.duration })})
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationIcon fontSize="small" color="action" />
              <Typography variant="body2">
                {activity.location.name}, {activity.location.address}, {activity.location.city}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GroupIcon fontSize="small" color="action" />
              <Typography variant="body2">
                {activity.numberOfParticipants} {activity.numberOfParticipants > 1 ? t('participants') : t('participant')}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* User Profile Section */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, height: '100%' }}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="h5" gutterBottom>
                  {t('userProfile')}
                </Typography>
                <Typography variant="body1">
                  {userData.user?.firstName} {userData.user?.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {userData.user?.email}
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom>
                  {t('subscription')}
                </Typography>
                {userData.subscription ? (
                  <>
                    <Typography variant="body1" gutterBottom>
                      {userData.subscription.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Chip
                        label={t('activitiesRemaining', {
                          remaining: userData.user?.remainingActivities || 0,
                          max: userData.user?.maxActivities || 0
                        })}
                        color={userData.user?.remainingActivities > 0 ? 'success' : 'error'}
                        variant="outlined"
                      />
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => navigate('/subscriptions')}
                      >
                        {t('manageSubscription')}
                      </Button>
                    </Box>
                  </>
                ) : (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t('noActiveSubscription')}
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => navigate('/subscriptions')}
                      >
                        {t('getSubscription')}
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom>
                  {t('newsletter')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {userData.user?.newsletterSubscribed
                    ? t('newsletterSubscribed')
                    : t('newsletterNotSubscribed')}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleNewsletterToggle}
                  >
                    {userData.user?.newsletterSubscribed
                      ? t('unsubscribe')
                      : t('subscribe')}
                  </Button>
                </Box>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Activities Section */}
        <Grid item xs={12} md={8}>
          <Stack spacing={4}>
            {/* Upcoming Activities */}
            <Paper elevation={0} sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                {t('upcomingActivities')}
              </Typography>
              {userData.upcomingActivities.length > 0 ? (
                userData.upcomingActivities.map((activity) => (
                  <ActivityCard
                    key={activity._id}
                    activity={activity}
                    showCancel
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('noUpcomingActivities')}
                </Typography>
              )}
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<EventIcon />}
                  onClick={() => navigate('/activities')}
                >
                  {t('browseActivities')}
                </Button>
              </Box>
            </Paper>

            {/* Past Activities */}
            <Paper elevation={0} sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                {t('pastActivities')}
              </Typography>
              {userData.pastActivities.length > 0 ? (
                userData.pastActivities.map((activity) => (
                  <ActivityCard key={activity._id} activity={activity} />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('noPastActivities')}
                </Typography>
              )}
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
};

export default UserDashboard; 